import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Injectable,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { NativeDateAdapter } from "@angular/material/core";
import { Subscription } from "rxjs";

/** Two-letter Swedish weekdays. Sunday first; the calendar rotates to Monday. */
@Injectable()
export class SwedishDateAdapter extends NativeDateAdapter {
  override getDayOfWeekNames(style: "long" | "short" | "narrow"): string[] {
    if (style === "narrow") {
      return ["sö", "må", "ti", "on", "to", "fr", "lö"];
    }
    return super.getDayOfWeekNames(style);
  }
}

@Component({
  selector: "dua-datetime-field",
  standalone: false,
  templateUrl: "./datetime-field.component.html",
  styleUrls: ["./datetime-field.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DatetimeFieldComponent implements OnDestroy {
  @Input() value: Date | null = null;
  @Input() mode: "date" | "time" = "date";
  @Input() disabled = false;
  @Input() inputId = "";

  @Output() valueChange = new EventEmitter<Date | null>();

  @ViewChild("origin") origin!: ElementRef<HTMLElement>;
  @ViewChild("panel") panel!: TemplateRef<unknown>;

  @HostBinding("class.is-date")
  get isDateMode(): boolean {
    return this.mode === "date";
  }

  @HostBinding("class.is-time")
  get isTimeMode(): boolean {
    return this.mode === "time";
  }

  panelAnchor = new Date();
  private overlayRef: OverlayRef | null = null;
  private closeSub: Subscription | null = null;

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef
  ) {}

  ngOnDestroy() {
    this.close();
  }

  get validValue(): Date | null {
    return this.value instanceof Date && !isNaN(this.value.getTime())
      ? this.value
      : null;
  }

  get display(): string {
    const date = this.validValue;
    if (!date) {
      return "";
    }
    if (this.mode === "time") {
      return `${this.pad(date.getHours())}:${this.pad(date.getMinutes())}`;
    }
    return `${date.getFullYear()}-${this.pad(date.getMonth() + 1)}-${this.pad(date.getDate())}`;
  }

  get hourText(): string {
    return this.pad(this.validValue ? this.validValue.getHours() : 0);
  }

  get minuteText(): string {
    return this.pad(this.validValue ? this.validValue.getMinutes() : 0);
  }

  get isOpen(): boolean {
    return this.overlayRef != null;
  }

  toggle() {
    if (this.disabled) {
      return;
    }
    if (this.overlayRef) {
      this.close();
    } else {
      this.open();
    }
  }

  onDay(date: Date | null) {
    if (!date) {
      return;
    }
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    this.focusCalendar(next);
    this.valueChange.emit(next);
    this.close();
  }

  stepHour(delta: number) {
    const next = this.baseDate();
    next.setHours((next.getHours() + delta + 24) % 24, next.getMinutes(), 0, 0);
    this.valueChange.emit(next);
  }

  stepMinute(delta: number) {
    const next = this.baseDate();
    next.setHours(next.getHours(), (next.getMinutes() + delta + 60) % 60, 0, 0);
    this.valueChange.emit(next);
  }

  private open() {
    this.panelAnchor = this.validValue ? new Date(this.validValue) : new Date();
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.origin)
      .withPositions([
        {
          originX: "start",
          originY: "bottom",
          overlayX: "start",
          overlayY: "top",
          offsetY: 4,
        },
        {
          originX: "start",
          originY: "top",
          overlayX: "start",
          overlayY: "bottom",
          offsetY: -4,
        },
        {
          originX: "end",
          originY: "bottom",
          overlayX: "end",
          overlayY: "top",
          offsetY: 4,
        },
      ])
      .withPush(true);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: "dua-datetime-backdrop",
      panelClass: "dua-datetime-overlay",
    });
    this.overlayRef.attach(new TemplatePortal(this.panel, this.viewContainerRef));
    this.closeSub = new Subscription();
    this.closeSub.add(this.overlayRef.backdropClick().subscribe(() => this.close()));
    this.closeSub.add(
      this.overlayRef.keydownEvents().subscribe((event) => {
        if (event.key === "Escape") {
          this.close();
        }
      })
    );
  }

  private close() {
    this.closeSub?.unsubscribe();
    this.closeSub = null;
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  private baseDate(): Date {
    const current = this.validValue;
    if (current) {
      return new Date(current);
    }
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private focusCalendar(date: Date) {
    const anchor = this.panelAnchor;
    if (
      anchor.getFullYear() !== date.getFullYear() ||
      anchor.getMonth() !== date.getMonth()
    ) {
      this.panelAnchor = new Date(date);
    }
  }

  private pad(value: number): string {
    return value.toString().padStart(2, "0");
  }
}
